// What the camera sees on a machine, next to what the machine reports. Super
// admins only. Times named *_at are in the machine's own time zone and carry no
// offset; *_utc are UTC.

export interface CameraFillLevel {
	slot_no: number;
	prod_id: string | null;
	name: string | null;
	fill_pct: number | null;
	measured_at: string | null;
	measured_at_utc: string | null;
}

export interface CameraFillLevels {
	machine_id: string;
	camera: {
		status: 'online' | 'offline';
		last_seen: string | null;
		last_seen_utc: string | null;
	} | null;
	levels: CameraFillLevel[];
}

export type RestockStatus = 'both' | 'machine_only' | 'camera_only';
export type RestockRange = '7d' | '14d' | '30d';

export interface MachineRestock {
	restocked_at: string;
	bags_used: number;
	grams_added: number | null;
	pct_added: number | null;
	level_before_pct: number | null;
	level_after_pct: number | null;
}

export interface CameraRestock {
	detected_at: string;
	grams_added: number | null;
	pct_added: number | null;
	level_after_pct: number | null;
}

export interface RestockComparison {
	slot_no: number;
	name: string | null;
	status: RestockStatus;
	minutes_apart: number | null;
	machine: MachineRestock | null;
	camera: CameraRestock | null;
}

export interface RestockComparisonResponse {
	machine_id: string;
	range: RestockRange;
	match_window_minutes: number;
	has_camera: boolean;
	// The camera's restocks could not be read; the machine's are still listed.
	camera_unavailable: boolean;
	restocks: RestockComparison[];
}

// The machine's own level for one canister over time, rebuilt from its restocks and the grams each
// drink dispensed. pct is null where the line breaks.
export interface MachineLevelHistory {
	machine_id: string;
	slot_no: number;
	name: string | null;
	range: RestockRange;
	points: { at: string; pct: number | null }[];
}

export const RESTOCK_RANGES: { value: RestockRange; label: string }[] = [
	{ value: '7d', label: '7 days' },
	{ value: '14d', label: '14 days' },
	{ value: '30d', label: '30 days' }
];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function cameraLevelsBySlot(levels: CameraFillLevel[]): { [slotNo: number]: CameraFillLevel } {
	const bySlot: { [slotNo: number]: CameraFillLevel } = {};
	(levels || []).forEach(level => {
		if (level && level.slot_no !== null && level.slot_no !== undefined) {
			bySlot[level.slot_no] = level;
		}
	});
	return bySlot;
}

// "Oct 21, 3:41 PM" from a time with no offset, read as written.
export function formatLocalDateTime(naiveIso: string | null): string {
	const match = naiveIso ? naiveIso.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/) : null;
	if (!match) {
		return '—';
	}
	const hour = Number(match[4]);
	const hour12 = hour % 12 === 0 ? 12 : hour % 12;
	return `${MONTHS[Number(match[2]) - 1]} ${Number(match[3])}, ${hour12}:${match[5]} ${hour < 12 ? 'AM' : 'PM'}`;
}

// "Oct 20" from a time with no offset, read as written.
export function formatLocalDate(naiveIso: string | null, withYear = false): string {
	const match = naiveIso ? naiveIso.match(/^(\d{4})-(\d{2})-(\d{2})/) : null;
	if (!match) {
		return '';
	}
	const day = `${MONTHS[Number(match[2]) - 1]} ${Number(match[3])}`;
	return withYear ? `${day}, ${match[1]}` : day;
}

// BrewVision counts as working when it reports itself online and was seen in the last day: its
// online flag alone can stay set long after it stops reporting.
const BREWVISION_SEEN_WITHIN_MS = 24 * 60 * 60 * 1000;

export function isBrewVisionWorking(status: string | null, lastSeenUtc: string | null, now: Date = new Date()): boolean {
	if (status !== 'online' || !lastSeenUtc) {
		return false;
	}
	return now.getTime() - new Date(lastSeenUtc).getTime() <= BREWVISION_SEEN_WITHIN_MS;
}

// How long ago a camera reading was taken: "just now", "25 min ago", "3 h ago",
// "2 d ago", then the date once it is more than a week old. The date is the
// machine's own (localIso) so it matches the times shown beside it.
export function formatReadingAge(utcIso: string | null, now: Date = new Date(), localIso: string | null = null): string {
	const at = utcIso ? new Date(utcIso) : null;
	if (!at || isNaN(at.getTime())) {
		return '';
	}
	const minutes = Math.floor((now.getTime() - at.getTime()) / 60000);
	if (minutes < 1) {
		return 'just now';
	}
	if (minutes < 60) {
		return `${minutes} min ago`;
	}
	const hours = Math.floor(minutes / 60);
	if (hours < 24) {
		return `${hours} h ago`;
	}
	const days = Math.floor(hours / 24);
	if (days <= 7) {
		return `${days} d ago`;
	}
	return `on ${formatLocalDate(localIso) || `${MONTHS[at.getUTCMonth()]} ${at.getUTCDate()}`}`;
}

export function formatPct(value: number | null): string {
	return value === null || value === undefined ? '—' : `${Math.round(value)}%`;
}

// Signed: a level that went down shows as "−3%", not "+-3%".
export function formatPctAdded(value: number | null): string {
	if (value === null || value === undefined) {
		return '—';
	}
	const rounded = Math.round(value);
	return `${rounded < 0 ? '−' : '+'}${Math.abs(rounded)}%`;
}

export function formatBags(bags: number | null): string {
	if (bags === null || bags === undefined) {
		return '';
	}
	const rounded = Math.round(bags * 100) / 100;
	return `${rounded} ${rounded === 1 ? 'bag' : 'bags'}`;
}

// How far apart the two amounts are, in points of the canister: positive when
// the machine says more went in than the camera saw.
export function restockDifference(restock: RestockComparison): number | null {
	if (!restock.machine || !restock.camera) {
		return null;
	}
	const machinePct = restock.machine.pct_added;
	const cameraPct = restock.camera.pct_added;
	if (machinePct === null || machinePct === undefined || cameraPct === null || cameraPct === undefined) {
		return null;
	}
	return Math.round(machinePct - cameraPct);
}

// Restocks within this many minutes of each other were one stop at the machine.
export const VISIT_GAP_MINUTES = 30;

// A gap this size between what the machine logged and what the camera saw is worth a look.
export const NOTABLE_DIFFERENCE_POINTS = 10;

// The canisters restocked at one stop. at is the first restock's time, in the machine's time zone.
export interface RestockVisit {
	at: string;
	restocks: RestockComparison[];
}

export interface CameraNote {
	kind: 'agrees' | 'less' | 'more' | 'not_seen';
	text: string;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Times with no offset compare and subtract as if they were UTC; only differences matter here.
function naiveMs(naiveIso: string): number {
	return Date.parse(`${naiveIso}Z`);
}

export function restockTime(restock: RestockComparison): string {
	return restock.machine ? restock.machine.restocked_at : restock.camera.detected_at;
}

// Newest stop first; canisters within a stop in the order they were restocked.
export function groupRestockVisits(restocks: RestockComparison[]): RestockVisit[] {
	const sorted = (restocks || []).slice().sort((a, b) => naiveMs(restockTime(a)) - naiveMs(restockTime(b)));
	const visits: RestockVisit[] = [];
	let lastAt: number = null;
	sorted.forEach(restock => {
		const at = naiveMs(restockTime(restock));
		const visit = visits[visits.length - 1];
		if (visit && lastAt !== null && at - lastAt <= VISIT_GAP_MINUTES * 60000) {
			visit.restocks.push(restock);
		} else {
			visits.push({ at: restockTime(restock), restocks: [restock] });
		}
		lastAt = at;
	});
	return visits.reverse();
}

// "Tue, Oct 21 · 3:41 PM" from a time with no offset, read as written.
export function formatVisitTime(naiveIso: string | null): string {
	const match = naiveIso ? naiveIso.match(/^(\d{4})-(\d{2})-(\d{2})/) : null;
	if (!match) {
		return '—';
	}
	const weekday = WEEKDAYS[new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))).getUTCDay()];
	return `${weekday}, ${formatLocalDateTime(naiveIso).replace(', ', ' · ')}`;
}

// What the operator logged: "1 bag · 26% → 100%".
export function describeMachineRestock(machine: MachineRestock): string {
	const levels = machine.level_before_pct !== null && machine.level_after_pct !== null
		? `${formatPct(machine.level_before_pct)} → ${formatPct(machine.level_after_pct)}`
		: `to ${formatPct(machine.level_after_pct)}`;
	return `${formatBags(machine.bags_used)} · ${levels}`;
}

// BrewVision's view of a logged restock, worded so a match stays quiet and a gap stands out.
// The text follows the BrewVision name, which the page shows as its wordmark.
export function cameraNote(restock: RestockComparison): CameraNote | null {
	if (!restock.machine) {
		return null;
	}
	if (!restock.camera) {
		return { kind: 'not_seen', text: 'didn\'t see this restock' };
	}
	const amount = formatPctAdded(restock.camera.pct_added);
	const points = restockDifference(restock);
	if (points !== null && points >= NOTABLE_DIFFERENCE_POINTS) {
		return { kind: 'less', text: `saw less: ${amount}` };
	}
	if (points !== null && points <= -NOTABLE_DIFFERENCE_POINTS) {
		return { kind: 'more', text: `saw more: ${amount}` };
	}
	return { kind: 'agrees', text: amount };
}

// The most recent restock of a canister. A refill only the camera saw counts when camera readings show.
export function latestRestock(restocks: RestockComparison[], slotNo: number, includeUnlogged: boolean): RestockComparison | null {
	let latest: RestockComparison = null;
	(restocks || []).forEach(restock => {
		if (restock.slot_no !== slotNo || (!restock.machine && !includeUnlogged)) {
			return;
		}
		if (!latest || naiveMs(restockTime(restock)) > naiveMs(restockTime(latest))) {
			latest = restock;
		}
	});
	return latest;
}

// "Restocked Oct 21 · 1 bag", or "Filled Oct 21 · not logged" for a refill only the camera saw.
export function describeLatestRestock(restock: RestockComparison | null, rangeLabel: string): string {
	if (!restock) {
		return `No restock in the last ${rangeLabel}`;
	}
	const date = formatLocalDate(restockTime(restock));
	return restock.machine
		? `Restocked ${date} · ${formatBags(restock.machine.bags_used)}`
		: `Filled ${date} · not logged`;
}

// A refill BrewVision saw that nobody logged: the machine's level for this canister is likely off.
// Follows the BrewVision name, like cameraNote.
export function unloggedRestockText(restock: RestockComparison): string {
	return `saw it filled to ${formatPct(restock.camera.level_after_pct)}`;
}

// Whether camera readings show on a machine's pages. On unless a super admin hid them in this browser.
const SHOW_CAMERA_READINGS_KEY = 'vb.machine.showCameraReadings';

export function readShowCameraReadings(): boolean {
	try {
		return localStorage.getItem(SHOW_CAMERA_READINGS_KEY) !== '0';
	} catch (e) {
		return true;
	}
}

export function writeShowCameraReadings(show: boolean): void {
	try {
		localStorage.setItem(SHOW_CAMERA_READINGS_KEY, show ? '1' : '0');
	} catch (e) {}
}
